<template>
	<v-row class="full-height">
		<v-col cols="12" class="flex-none">
			<div class="package-actions">
				<v-btn
					v-if="canManagePackageCategories"
					depressed
					outlined
					@click="duplicatePackage()"
					:disabled="duplicateActive || packageData.packageCategoryId == null"
					>Duplicate</v-btn
				>
				<v-btn
					v-if="canManagePackageCategories"
					depressed
					outlined
					@click="deleteWarning = !deleteWarning"
					:disabled="packageData.packageCategoryId == null"
					>Delete</v-btn
				>
				<v-btn depressed outlined @click="$router.push('/inventory/package-category')"
					>Cancel</v-btn
				>
				<v-btn
					v-if="canManagePackageCategories"
					depressed
					color="success"
					@click="savePackageCategory()"
					>Save</v-btn
				>
			</div>
		</v-col>

		<v-col cols="6" class="flex-auto mx-auto">
			<div class="package-form">
				<div class="form-input">
					<label>Package Category Name</label>
					<v-text-field
						outlined
						dense
						hide-details="auto"
						v-model="packageData.name"
						:disabled="!canManagePackageCategories"
					></v-text-field>
				</div>

				<div class="form-input">
					<label>Description</label>
					<v-text-field
						outlined
						dense
						hide-details="auto"
						v-model="packageData.description"
						:disabled="!canManagePackageCategories"
					></v-text-field>
				</div>
				<div class="form-input">
					<label>System Category</label>
					<v-select
						v-model="packageData.systemCategoryId"
						:items="$systemCategories.systemCategories"
						item-text="text"
						item-value="systemCategoryId"
						dense
						outlined
						hide-details="auto"
						@change="searchResults = []"
						:disabled="disabled"
					></v-select>
				</div>
			</div>

			<div class="package-sizes">
				<div class="package-sizes-description">
					<p><em>Deleting a package will NOT affect any past orders</em></p>
				</div>
				<div class="package-sizes-table">
					<div class="flex-table" role="table-head">
						<div class="flex-table-row" role="row-group">
							<div class="flex-table-cell" role="column-header">&nbsp;</div>
							<div class="flex-table-cell" role="column-header">Available Items</div>
							<div class="flex-table-cell" role="column-header">Description</div>
							<div class="flex-table-cell" role="column-header">&nbsp;</div>
						</div>
					</div>
					<div class="flex-table" role="table-body">
						<div
							v-for="(data, i) in packageData.items"
							:key="i"
							class="flex-table-row"
							role="row-group"
						>
							<div class="flex-table-cell" role="cell">{{ i + 1 }}</div>
							<div class="flex-table-cell" role="cell">
								<div class="form-input">
									<v-combobox
										prepend-inner-icon="mdi-magnify"
										dense
										outlined
										placeholder="Search Style Codes"
										hide-details="auto"
										v-model="packageData.items[i]"
										:items="searchResults"
										item-text="styleCode"
										:disabled="!packageData.systemCategoryId || !canManagePackageCategories"
										@keyup="searchInventoryItems($event.srcElement.value)"
									>
										<template v-slot:item="{ item, attrs, on }">
											<v-list-item v-on="on" v-bind="attrs">
												<v-list-item-content>
													<v-list-item-title>
														<v-row no-gutters align="center">
															<SystemCategoryIcon :system-category-id="item.systemCategoryId" />
															{{ `${item.styleCode} - ${item.description}` }}
														</v-row>
													</v-list-item-title>
												</v-list-item-content>
											</v-list-item>
										</template>
									</v-combobox>
								</div>
							</div>
							<div class="flex-table-cell" role="cell">
								<div class="form-input">
									<v-text-field
										outlined
										dense
										hide-details="auto"
										:value="data?.description"
										disabled
									></v-text-field>
								</div>
							</div>
							<div class="flex-table-cell" role="cell">
								<v-btn
									:disabled="!canManagePackageCategories"
									icon
									color="primary"
									@click="removePackageRow(data)"
									><v-icon style="font-size: 2rem">mdi-delete</v-icon>
								</v-btn>
							</div>
						</div>
					</div>
				</div>
				<div class="package-sizes-actions">
					<v-btn v-if="canManagePackageCategories" text @click="addPackageRow()"
						><v-icon>mdi-plus</v-icon> Add a Row</v-btn
					>
				</div>
			</div>
		</v-col>

		<WarningModal
			v-if="deleteWarning"
			:showModal="deleteWarning"
			:message="'Are you sure you want to delete this package category?'"
			:cancelText="'No, Cancel'"
			:confirmText="'Yes, Delete'"
			@cancel="deleteWarning = false"
			@confirm="
				deletePackageCategory();
				deleteWarning = false;
			"
		/>
	</v-row>
</template>

<script>
import gql from "graphql-tag";
import WarningModal from "@/components/WarningModal.vue";
import SystemCategoryIcon from "@/components/SystemCategoryIcon.vue";
import { hasPermission, SystemPermissions } from "@/models/User";
import { getApiUrl } from "@/helpers";

export default {
	name: "PackageCategoryView",
	props: {
		packageCategoryId: {
			type: Number,
			default: null,
		},
	},
	components: {
		WarningModal,
		SystemCategoryIcon,
	},
	data() {
		return {
			packageData: {
				packageCategoryId: null,
				systemCategoryId: null,
				name: "",
				description: "",
				items: [
					{
						inventoryItemId: null,
						styleCode: "",
						description: "",
					},
				],
			},
			systemCategories: [],
			searchResults: [],
			duplicateActive: false,
			deleteWarning: false,
		};
	},
	computed: {
		disabled() {
			return this.packageData.items.filter((el) => el?.styleCode).length > 0;
		},
		canManagePackageCategories() {
			const user = this.$store.getters.userInfo;
			return (
				hasPermission(user, SystemPermissions.Inventory_PackageCategory_Create_Update_Delete) ||
				user.isAdmin
			);
		},
	},
	methods: {
		addPackageRow() {
			let newPackage = {
				inventoryItemId: null,
				styleCode: "",
				description: "",
			};

			this.packageData.items.push(newPackage);
		},

		removePackageRow(rowData) {
			//filter out the passed in rowData
			this.packageData.items = this.packageData.items.filter((obj) => obj !== rowData);
		},

		duplicatePackage() {
			//remove the package id so we can save a new copy and replace the name
			this.packageData.packageCategoryId = null;
			this.packageData.name = `${this.packageData.name} (DUPLICATE)`;

			//set duplcate state
			this.duplicateActive = true;
		},

		async deletePackageCategory() {
			try {
				const res = await this.$http.delete(
					getApiUrl(`/api/settings/package-categories/${this.packageData.packageCategoryId}`)
				);
				console.log(res);
				this.$router.push("/inventory/package-category");
			} catch (error) {
				console.log(error);
			}
		},

		async getPackageById() {
			try {
				const res = await this.$apollo.query({
					query: gql`query {
				        packageCategoryById(id: ${this.packageCategoryId}) {
				            packageCategoryId
                            name
                            description
                            systemCategoryId
                            items {
                                inventoryItemId
                                styleCode
                                description
                            }         
				        }
				    }`,
					fetchPolicy: "no-cache",
				});

				console.log("getting packages", res);
				this.packageData = res.data.packageCategoryById;
			} catch (error) {
				console.log(error);
			}
		},

		async savePackageCategory() {
			try {
				//create post/put request to add/update package category to api
				let res;
				let packageCategory = {
					packageCategoryId: this.packageData.packageCategoryId,
					systemCategoryId: this.packageData.systemCategoryId,
					name: this.packageData.name,
					description: this.packageData.description,
					inventoryItemIds: this.packageData.items.map((obj) => obj.inventoryItemId),
				};
				if (this.packageData.packageCategoryId) {
					res = await this.$http.put(
						getApiUrl("/api/settings/package-categories"),
						packageCategory
					);
				} else {
					res = await this.$http.post(
						getApiUrl("/api/settings/package-categories"),
						packageCategory
					);
				}
				console.log("saving category", res);
				this.$router.push("/inventory/package-category");
			} catch (error) {
				console.log(error);
			}
		},

		async searchInventoryItems(searchTerm) {
			try {
				//send gql query to get current inventory items related to searchTerm
				const res = await this.$apollo.query({
					query: gql`
						query inventoryListings($searchTerm: String) {
							inventoryListings(searchTerm: $searchTerm, skip: 0, take: 500, order: { styleCode: ASC }, where: { systemCategoryId: { eq: ${this.packageData.systemCategoryId} } }) {
								items {
									inventoryItemId
									systemCategoryId
									styleCode
									description
								}
								pageInfo {
									hasNextPage
									hasPreviousPage
								}
							}
						}
					`,
					variables: {
						searchTerm,
					},
					fetchPolicy: "no-cache",
				});

				console.log(`searching for packages with ${searchTerm}`, res.data.inventoryListings.items);
				this.searchResults = res.data.inventoryListings.items;
			} catch (error) {
				console.log(error);
			}
		},
	},
	async mounted() {
		this.systemCategories = this.systemCategories.topLevelItemCategories;

		if (this.packageCategoryId) {
			await this.getPackageById();
		}
	},
};
</script>

<style lang="scss" scoped>
.package {
	&-actions {
		display: flex;
		justify-content: flex-end;

		.v-btn {
			background-color: $color-white;
			border-color: $color-gray;
			margin-left: 0.5rem;
		}
	}

	&-sizes {
		display: flex;
		flex-flow: column nowrap;
		height: 100%;
		margin-top: 2rem;

		&-description {
			p {
				color: $color-gray;
				font-size: 1.4rem;
			}
		}

		&-table {
			flex-grow: 1;
			background-color: $color-white;

			.flex-table-cell {
				.form-input {
					width: 100%;
				}

				&:nth-child(1) {
					width: 8%;
					justify-content: center;
				}
				&:nth-child(2) {
					width: 42%;
				}
				&:nth-child(3) {
					width: 42%;
				}
				&:nth-child(4) {
					width: 8%;
					justify-content: center;
				}
			}
		}

		&-actions {
			margin-top: 1rem;

			.v-btn {
				font-size: 1.4rem;
				padding-left: 1rem;

				.v-icon {
					font-size: 2rem;
					margin-right: 0.5rem;
				}
			}
		}
	}
}
</style>
